import { ICellEditorAngularComp } from 'ag-grid-angular';
import { GridApi } from 'ag-grid-enterprise';
import { AfterViewInit, Component, Inject, Injector, ViewChild } from '@angular/core';
import { digger } from '@cbre/digger';
import { SelectComponent } from '@shared/components/inputs/select/select.component';
import { LookupRefService } from '@shared/services/lookup-ref.service';
import { of } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';
import { AgGridOptionsEditType } from '../ag-table-grid-options';
import { collapseList } from '@shared/functions';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

export interface DropdownPlugin {
  getValues(params: any): any;
}

export type Option = { value?: string; disabled?: boolean; [key: string]: unknown };

export type Params = { allOptions?: Option[]; requirementOrigin?: string };

@Component({
  selector: 'miq-ag-select',
  styleUrls: ['./ag-select.component.scss'],
  templateUrl: './ag-select.component.html',
  standalone: false,
})
export class AgSelectComponent implements ICellEditorAngularComp, AfterViewInit {
  params: any;
  values$: any;
  currentValue: any;
  column;
  state = '';
  apiGrid: GridApi;
  dynamicValues: any;
  cascadingObjectField: string;
  private focusWhenCreate = false;

  @ViewChild('input', { read: SelectComponent, static: false }) public input: SelectComponent;

  constructor(
    private readonly lookupRef: LookupRefService,
    private readonly injector: Injector,
    @Inject(CbreLoggerToken) private readonly logger: CbreLogger
  ) {}

  /**
   * Required by ICellEditorAngularComp
   */
  agInit(params: any) {
    this.apiGrid = params.api;
    this.params = params;
    const paramsObject =
      typeof params?.cellEditorParams === 'function' ? params?.cellEditorParams(params) : params?.cellEditorParams;
    this.currentValue = Array.isArray(params.value) ? params.value[0] : params.value;
    // ag grid collisions with miq valueSetter, we need to remove it so it won't be called by miq components
    // eslint-disable-next-line no-unused-vars
    const { valueSetter, ...colDef } = params.colDef;
    this.column = colDef;
    this.dynamicValues = params.dynamicValues;
    this.focusWhenCreate = !!params.focusWhenCreate || paramsObject?.focusWhenCreate;
    if (paramsObject?.getValues) {
      this.values$ = paramsObject.getValues(this.lookupRef);
    } else {
      this.setUpDropdowns();
    }
  }

  private setUpDropdowns() {
    const { lookupCategory, cascadingObjectField } = this.column;
    const { values } = this.column;

    if (cascadingObjectField) {
      const cascadingValue = digger(this.params.data, cascadingObjectField);
      const lookupPayload = [cascadingValue];
      const url = this.#getLookupCategory(lookupPayload);
      this.values$ = this.lookupRef.loadDataFor(url, false, false, lookupPayload);
      return;
    }

    if (!values) {
      if (this.dynamicValues != null) {
        this.values$ = of(this.dynamicValues ?? []);
        return;
      }
      if (lookupCategory) {
        this.values$ = this.initAsyncOptions(lookupCategory).pipe(
          switchMap(options => this.applyPluginIfNeeded(options))
        );
        return;
      }

      // No values, dynamicValues, or lookupCategory - return empty array
      this.values$ = of([]);
      return;
    }
    this.values$ = this.applyPluginIfNeeded(values);
  }

  private applyPluginIfNeeded(options: Record<string, any>[]) {
    if (this.params?.componentPlugin) {
      return this.handlePlugin(this.params.componentPlugin, options);
    }
    return of(options ?? []);
  }

  private handlePlugin(pluginConfig: any, options) {
    const { Type, sourceFields } = pluginConfig;
    try {
      const pluginService = this.injector.get<DropdownPlugin>(Type);
      const params = { allOptions: options, ...this.getSourceFieldValues(sourceFields) };

      return pluginService.getValues(params);
    } catch (error) {
      this.logger.error({ eventName: 'Error initializing plugin', error });
      return of([]);
    }
  }

  private getSourceFieldValues(fields) {
    if (!fields || !Array.isArray(fields)) {
      return {};
    }
    const extractedValues = {};
    fields.forEach(fieldConfig => {
      const { field, paramName } = fieldConfig;
      if (field && paramName) {
        extractedValues[paramName] = digger(this.params.data, field);
      }
    });
    return extractedValues;
  }

  #getLookupCategory(value: any[]) {
    const lookupUrl = this.column.lookupCategory ?? null;
    const values = collapseList(value);
    return `${lookupUrl}?_cascade=${encodeURIComponent(JSON.stringify(values))}`;
  }

  private initAsyncOptions(lookupCategory) {
    this.state = 'shared.loading';
    return this.lookupRef.loadDataFor(lookupCategory).pipe(finalize(() => (this.state = '')));
  }

  setCurrentValue(value: any) {
    this.currentValue = value;
    // Grid options are read by the AG Grind once on the initialization
    // only the context object gets updated
    const editType = this.params.context?.editType ?? this.params.gridOptions?.editType;
    if (editType !== AgGridOptionsEditType.FullRow) {
      this.apiGrid.stopEditing();
    }
  }

  /**
   * Required by ICellEditorAngularComp
   */
  ngAfterViewInit() {
    if (this.focusWhenCreate && this.params.gridOptions?.editType !== AgGridOptionsEditType.FullRow) {
      setTimeout(() => this.input.matSelect.focus());
    }
  }

  ngOnDestroy() {
    this.input = null;
  }

  /**
   * Required by ICellEditorAngularComp
   */
  getValue() {
    return this.currentValue;
  }
}
